import { useInView } from 'react-intersection-observer';
import { useRef, type ReactNode } from 'react';

interface MountWhenVisibleProps {
  children: ReactNode;
  /**
   * Mount now, without waiting for the wrapper to scroll into view — for a subtree
   * that something outside the viewport already depends on (the Well page's rail
   * hosts the Subsurface Explorer's controls, so the Explorer has to exist while its
   * card is still far down the column). Sticky: once the children have mounted for
   * any reason they stay mounted, so a host that later goes away does not unmount them.
   */
  eager?: boolean;
  /** Shown in place of the children until the wrapper scrolls near the viewport. */
  placeholder?: ReactNode;
  /** Distance from the viewport at which to mount, so heavy content is ready just
   *  before it scrolls into view rather than after. */
  rootMargin?: string;
  /** Min-height of the placeholder so the sentinel occupies space and is observable. */
  minHeight?: number | string;
  className?: string;
}

/**
 * Defers mounting `children` until the wrapper scrolls near the viewport, then
 * mounts them permanently (triggerOnce). Behaviour-preserving: once visible it
 * renders exactly `children`, so the wrapped subtree is identical to mounting it
 * eagerly — only *when* it mounts changes.
 *
 * Built on the shared `react-intersection-observer` `useInView` hook, and falls
 * back to mounting eagerly where IntersectionObserver is unavailable (e.g. SSR).
 * Use to keep an expensive, below-the-fold subtree — its render work and its
 * network fetches — off the initial page-load path.
 * @param {MountWhenVisibleProps} props Component props.
 * @return {JSX.Element} The children once visible, else the placeholder sentinel.
 */
export const MountWhenVisible = ({
  children,
  eager = false,
  placeholder = null,
  rootMargin = '200px',
  minHeight = 320,
  className,
}: MountWhenVisibleProps) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin,
    fallbackInView: true,
  });

  // Remembers that the children have mounted, so neither `eager` flipping back off
  // nor the observer matters afterwards — mounting is one-way, as with `triggerOnce`.
  const mounted = useRef(false);
  if (eager || inView) mounted.current = true;

  if (mounted.current) return <>{children}</>;

  return (
    <div ref={ref} className={className} style={{ minHeight }}>
      {placeholder}
    </div>
  );
};
